import {
  CLAVE_ULTIMO_SYNC,
  db,
  escribirMeta,
  leerMeta,
  vaciarBaseLocal,
  type ConflictoLocal,
  type MutacionPendiente,
} from './db.js'
import { uuidv7 } from './uuid.js'

/**
 * El motor de sincronización. **El único lugar de la PWA que toca la red.**
 *
 * Todo lo demás lee de Dexie. Si algún día un componente necesita hacer `fetch`
 * para mostrar algo, lo que hay que cambiar es este archivo para que ese dato
 * llegue a Dexie — no el componente (§5.5).
 *
 * El ciclo es: empujar lo pendiente, después traer lo nuevo. En ese orden, y no
 * al revés: si primero trajera, el pull pisaría con la versión del servidor una
 * cita que todavía no subió, y el usuario vería desaparecer lo que acaba de
 * escribir.
 */

const BASE = '/api'

export type EstadoSync = 'inactivo' | 'sincronizando' | 'error' | 'sin_conexion'

export interface ResumenSync {
  estado: EstadoSync
  ultimoSync: string | null
  pendientes: number
  conflictos: number
  error?: string
}

type Escucha = (resumen: ResumenSync) => void

const escuchas = new Set<Escucha>()
let estadoActual: EstadoSync = 'inactivo'
let ultimoError: string | undefined

export function suscribirse(fn: Escucha): () => void {
  escuchas.add(fn)
  void notificar()
  return () => escuchas.delete(fn)
}

async function notificar(): Promise<void> {
  const resumen: ResumenSync = {
    estado: estadoActual,
    ultimoSync: await leerMeta(CLAVE_ULTIMO_SYNC),
    pendientes: await db.outbox.count(),
    conflictos: await db.conflictos.where('resuelto').equals(0).count(),
    ...(ultimoError ? { error: ultimoError } : {}),
  }
  for (const fn of escuchas) fn(resumen)
}

/** De qué tenant son los datos que hay guardados. */
const CLAVE_TENANT = 'tenant_id'

/** Con qué versión del canal operativo se llenó esta base local. */
const CLAVE_CANAL = 'version_canal'

/**
 * La versión del canal: **qué tablas trae el pull**.
 *
 * Se sube cada vez que el pull empieza a mandar una tabla que antes no mandaba.
 * Sin esto, un dispositivo que ya sincronizó no recibe nunca las filas viejas de
 * la tabla nueva: el pull es incremental por `updated_at` y esas filas no
 * cambiaron, así que la tabla local queda vacía para siempre.
 *
 * No es hipotético: es lo que pasó con `asignacion_espacio`. El canal no la
 * mandaba, el motor local la exige para asignar consultorio (§3.8), y el
 * resultado era una agenda que contestaba "no hay lugar" con el día entero
 * libre y marcaba "sin consultorio" a los cinco profesionales.
 *
 *   1  el canal original
 *   2  entra `asignacion_espacio`
 *   4  entra `servicio_tramo_persona` (duración y buffer por profesional, T32c)
 *   5  entran especialidad y persona_especialidad (T30d)
 */
const VERSION_CANAL = '5'

/**
 * Prepara la base local para este tenant, y la vacía si los datos son de otro.
 *
 * Hace falta por algo que el sync incremental no puede resolver solo: **sólo se
 * entera de los borrados que llegan con `deleted_at`**. Si la base del servidor
 * se limpia por fuera —un backup restaurado, un tenant recreado, un TRUNCATE en
 * desarrollo— esas filas desaparecen sin tombstone, y el cliente se queda con
 * ellas para siempre. En pantalla eso se ve como cada profesional duplicado y
 * cada cita dos veces, sin ninguna pista de por qué.
 *
 * Y hay un motivo más serio: si alguien entra a otra clínica desde el mismo
 * navegador, dejar los datos de la anterior es una fuga.
 *
 * Lo mismo vale para la versión del canal: si el pull empezó a mandar una tabla
 * nueva, lo que ya está guardado está incompleto y no hay pull incremental que
 * lo complete. Ahí también hay que bajar todo de nuevo, pero **conservando el
 * outbox**: el trabajo sin subir no se pierde porque cambió el canal.
 *
 * Devuelve `true` si tuvo que vaciar.
 */
export async function prepararParaTenant(tenantId: string): Promise<boolean> {
  const guardado = await leerMeta(CLAVE_TENANT)
  const canal = await leerMeta(CLAVE_CANAL)

  if (guardado === tenantId && canal === VERSION_CANAL) return false

  if (guardado !== null && guardado !== tenantId) {
    // Datos de otra clínica: se van enteros, el outbox incluido. Subir a la
    // clínica B una cita escrita para la A sería peor que perderla.
    await vaciarBaseLocal()
  } else if (guardado === tenantId) {
    // Mismo tenant, canal viejo: se rehace la base sin tocar lo pendiente.
    await vaciarBaseLocal({ conservarOutbox: true })
  }

  await escribirMeta(CLAVE_TENANT, tenantId)
  await escribirMeta(CLAVE_CANAL, VERSION_CANAL)
  return guardado !== null
}

/**
 * Tira los datos locales y vuelve a bajar todo desde cero.
 *
 * Es la salida cuando lo local quedó inconsistente con el servidor por algo
 * que el sync no puede reconciliar. Las mutaciones pendientes se conservan:
 * son trabajo que todavía no subió y perderlo sería peor que el problema.
 */
export async function resincronizarDesdeCero(): Promise<void> {
  const tenantId = await leerMeta(CLAVE_TENANT)

  await vaciarBaseLocal({ conservarOutbox: true })

  if (tenantId) await escribirMeta(CLAVE_TENANT, tenantId)
  // La versión del canal se reescribe acá también: si no, el próximo arranque
  // encontraría la marca borrada y volvería a rehacer la base al vacío.
  await escribirMeta(CLAVE_CANAL, VERSION_CANAL)

  await sincronizar()
}

// ─── Outbox ────────────────────────────────────────────────────────

/**
 * Encola una escritura. Devuelve el `mutationId` para que quien la encoló pueda
 * pintar la fila como tentativa (§5.3) y ligarla después con la real.
 */
export async function encolar(
  tipo: MutacionPendiente['tipo'],
  cuerpo: unknown,
): Promise<string> {
  const mutationId = uuidv7()
  await db.outbox.add({
    mutationId,
    tipo,
    cuerpo,
    creadaEn: new Date().toISOString(),
    intentos: 0,
    proximoIntento: new Date().toISOString(),
  })
  void notificar()
  void sincronizar()
  return mutationId
}

/**
 * Backoff exponencial con techo. El techo importa: sin él, una tablet que pasó
 * la noche sin red vuelve con un reintento programado para dentro de horas y
 * la recepcionista no entiende por qué no sube nada.
 */
function proximoIntento(intentos: number): string {
  const segundos = Math.min(2 ** intentos, 300)
  return new Date(Date.now() + segundos * 1000).toISOString()
}

async function empujarPendientes(): Promise<void> {
  const ahora = new Date().toISOString()
  const listas = await db.outbox.where('proximoIntento').belowOrEqual(ahora).toArray()
  if (listas.length === 0) return

  // De a lotes, en orden de creación: mover una cita antes de crearla no tiene
  // sentido, y el orden del outbox es el orden en que ocurrieron las cosas.
  const lote = listas
    .sort((a, b) => {
      const porFecha = a.creadaEn.localeCompare(b.creadaEn)
      if (porFecha !== 0) return porFecha
      // Dos altas consecutivas pueden compartir el mismo milisegundo. Si la
      // cita referencia al paciente recién creado, el paciente debe viajar
      // primero aun en motores que no garanticen un sort estable.
      if (a.tipo === 'crear_cliente' && b.tipo === 'crear_cita') return -1
      if (a.tipo === 'crear_cita' && b.tipo === 'crear_cliente') return 1
      // Un pago offline puede referenciar un cargo creado en el mismo gesto.
      // Si comparten milisegundo, el cargo tiene que llegar primero.
      if (a.tipo === 'crear_cargo' && b.tipo === 'crear_pago') return -1
      if (a.tipo === 'crear_pago' && b.tipo === 'crear_cargo') return 1
      return a.mutationId.localeCompare(b.mutationId)
    })
    .slice(0, 50)

  const respuesta = await fetch(`${BASE}/sync`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      mutaciones: lote.map((m) => ({
        mutationId: m.mutationId,
        tipo: m.tipo,
        ...(m.cuerpo as Record<string, unknown>),
      })),
    }),
  })

  if (!respuesta.ok) {
    // Un fallo del lote entero no descarta nada: se reintenta con backoff.
    await db.transaction('rw', db.outbox, async () => {
      for (const m of lote) {
        await db.outbox.update(m.mutationId, {
          intentos: m.intentos + 1,
          proximoIntento: proximoIntento(m.intentos + 1),
          ultimoError: `HTTP ${respuesta.status}`,
        })
      }
    })
    throw new Error(`El servidor rechazó el lote: HTTP ${respuesta.status}`)
  }

  const { resultados } = (await respuesta.json()) as {
    resultados: {
      mutationId: string
      estado: 'aplicada' | 'duplicada' | 'conflicto' | 'rechazada'
      citaId?: string
      motivo?: string
      mensaje?: string
    }[]
  }

  // Se marca dentro del bucle y se actúa después: borrar el cursor dentro de la
  // misma transacción que escribe el conflicto lo dejaría a medias si algo falla.
  let deshacerOptimismo = false

  await db.transaction(
    'rw',
    [db.outbox, db.conflictos, db.cita, db.cita_tramo, db.reserva_recurso, db.meta],
    async () => {
    for (const r of resultados) {
      const original = lote.find((m) => m.mutationId === r.mutationId)

      if (r.estado === 'aplicada' || r.estado === 'duplicada') {
        // `duplicada` es éxito: el servidor ya la tenía. Es el caso normal
        // cuando la respuesta anterior se perdió en el camino.
        await db.outbox.delete(r.mutationId)
        if (original?.tipo === 'crear_cita') {
          const cuerpo = original.cuerpo as { citaId?: string }
          const citaId = r.citaId ?? cuerpo.citaId
          if (citaId) await limpiarCitaTentativa(citaId)
        }
        continue
      }

      if (r.estado === 'conflicto') {
        // El cambio ya se pintó en local —así funciona el optimismo— y el
        // servidor lo rechazó. Si no se deshace, la pantalla muestra una cita
        // donde no está, que es peor que no haberla movido nunca.
        //
        // El pull incremental no alcanza para arreglarlo: como el servidor no
        // cambió nada, no hay `updated_at` nuevo que traiga la versión real. Por
        // eso se borra el cursor y el próximo pull baja la ventana entera y pisa
        // lo local. Es caro, pero un conflicto es raro y la alternativa es que
        // la agenda mienta.
        deshacerOptimismo = true

        // No se resuelve solo: va a la cola de recepción con el slot original,
        // para que una persona decida (§5.3). CRDTs y merge automático están
        // descartados a propósito.
        const conflicto: ConflictoLocal = {
          mutationId: r.mutationId,
          tipo: original?.tipo ?? 'desconocido',
          cuerpo: original?.cuerpo ?? null,
          motivo: r.motivo ?? 'conflicto',
          mensaje: r.mensaje ?? 'El horario ya no está disponible.',
          detectadoEn: new Date().toISOString(),
          resuelto: false,
        }
        await db.conflictos.put(conflicto)
        if (original?.tipo === 'crear_cita') {
          const citaId = (original.cuerpo as { citaId?: string }).citaId
          if (citaId) await limpiarCitaTentativa(citaId)
        }
        await db.outbox.delete(r.mutationId)
        continue
      }

      // Rechazada: la mutación está mal y reintentarla no la va a arreglar.
      // Sacarla del outbox evita que bloquee la cola para siempre. Y lo local
      // también quedó adelantado respecto del servidor.
      deshacerOptimismo = true
      await db.conflictos.put({
        mutationId: r.mutationId,
        tipo: original?.tipo ?? 'desconocido',
        cuerpo: original?.cuerpo ?? null,
        motivo: r.motivo ?? 'rechazada',
        mensaje: r.mensaje ?? 'El servidor rechazó la operación.',
        detectadoEn: new Date().toISOString(),
        resuelto: false,
      })
      if (original?.tipo === 'crear_cita') {
        const citaId = (original.cuerpo as { citaId?: string }).citaId
        if (citaId) await limpiarCitaTentativa(citaId)
      }
      await db.outbox.delete(r.mutationId)
    }

    if (deshacerOptimismo) {
      // Sin cursor, el próximo pull trae la ventana completa y sobrescribe lo
      // que el cliente había adelantado. Va DENTRO de esta transacción: si se
      // borrara después y la pestaña muriera en el medio, el conflicto quedaría
      // registrado y lo local mostrando para siempre un cambio que el servidor
      // rechazó.
      await db.meta.delete(CLAVE_ULTIMO_SYNC)
    }
    },
  )
}

async function limpiarCitaTentativa(citaId: string): Promise<void> {
  const cita = await db.cita.get(citaId)
  if (!cita?.tentativa_local) return

  const tramos = await db.cita_tramo.where('cita_id').equals(citaId).toArray()
  const idsTramo = tramos.map((tramo) => tramo.id)
  if (idsTramo.length > 0) {
    const reservas = await db.reserva_recurso.where('cita_tramo_id').anyOf(idsTramo).primaryKeys()
    await db.reserva_recurso.bulkDelete(reservas)
    await db.cita_tramo.bulkDelete(idsTramo)
  }
  await db.cita.delete(citaId)
}

/**
 * Marca que el paciente llegó al mostrador.
 *
 * Idempotente en el cliente igual que en el servidor: si ya está marcado, no
 * escribe ni encola. La primera hora es el dato —cuánto hace que espera—; la
 * última sería sólo la última vez que alguien tocó el botón.
 *
 * Optimista: se ve en el acto y viaja cuando haya red. A las ocho de la mañana
 * la sala se llena igual que si internet estuviera caído.
 */
export async function marcarLlegada(citaId: string): Promise<void> {
  const cita = await db.cita.get(citaId)
  if (!cita || cita.llegada_at) return

  const llegadaAt = new Date().toISOString()
  await db.cita.update(citaId, { llegada_at: llegadaAt })
  await encolar('marcar_llegada', { citaId, llegadaAt })
}

// ─── Pull ──────────────────────────────────────────────────────────

interface RespuestaPull {
  corte: string
  datos: Record<string, unknown[]>
}

/** Mapea el nombre que devuelve la API a la tabla local. */
const TABLAS: Record<string, string> = {
  recursoTipos: 'recurso_tipo',
  recursos: 'recurso',
  horarios: 'recurso_horario',
  bloqueos: 'recurso_bloqueo',
  afinidades: 'recurso_afinidad',
  asignacionesEspacio: 'asignacion_espacio',
  capacidades: 'recurso_capacidad',
  servicios: 'servicio',
  tramosServicio: 'servicio_tramo',
  requisitos: 'servicio_tramo_req',
  serviciosTramoPersona: 'servicio_tramo_persona',
  clientes: 'cliente',
  citas: 'cita',
  tramos: 'cita_tramo',
  reservas: 'reserva_recurso',
  cargos: 'cargo',
  pagos: 'pago',
  flags: 'flag',
  especialidades: 'especialidad',
  personasEspecialidad: 'persona_especialidad',
}

async function traerCambios(): Promise<void> {
  const desde = await leerMeta(CLAVE_ULTIMO_SYNC)
  const url = desde ? `${BASE}/sync?desde=${encodeURIComponent(desde)}` : `${BASE}/sync`

  const respuesta = await fetch(url, { credentials: 'include' })
  if (!respuesta.ok) throw new Error(`El pull falló: HTTP ${respuesta.status}`)

  const { corte, datos } = (await respuesta.json()) as RespuestaPull

  await db.transaction('rw', db.tables, async () => {
    for (const [clave, filas] of Object.entries(datos)) {
      const nombreTabla = TABLAS[clave]
      if (!nombreTabla) continue
      const tabla = db.table(nombreTabla)

      for (const fila of filas as { id: string; deleted_at: string | null }[]) {
        // Un tombstone se aplica borrando la fila local. La fila remota se
        // conserva allá con su `deleted_at`; acá no hace falta guardarla,
        // porque el `desde` ya avanzó más allá de ese cambio.
        if (fila.deleted_at) {
          await tabla.delete(fila.id)
        } else {
          await tabla.put(normalizar(fila))
        }
      }
    }

    // El corte se guarda dentro de la misma transacción que los datos. Si se
    // guardara después y el navegador se cerrara en el medio, el próximo pull
    // arrancaría de un punto posterior a datos que nunca se escribieron.
    await db.meta.put({ clave: CLAVE_ULTIMO_SYNC, valor: corte })
  })
}

/** Las fechas llegan como string ISO y así se guardan: comparar strings ISO en UTC ordena igual que comparar fechas, y evita el baile de zonas dentro de IndexedDB. */
function normalizar(fila: Record<string, unknown>): Record<string, unknown> {
  const salida: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(fila)) {
    salida[k] = v instanceof Date ? v.toISOString() : v
  }
  return salida
}

// ─── Ciclo ─────────────────────────────────────────────────────────

let corriendo = false
let pendienteOtraVuelta = false

export async function sincronizar(): Promise<void> {
  if (!navigator.onLine) {
    estadoActual = 'sin_conexion'
    void notificar()
    return
  }

  // Una sola corrida a la vez. Dos pulls concurrentes escribirían el mismo
  // corte dos veces y podrían intercalar escrituras a la mitad.
  if (corriendo) {
    pendienteOtraVuelta = true
    return
  }

  corriendo = true
  estadoActual = 'sincronizando'
  ultimoError = undefined
  void notificar()

  try {
    // Empujar primero: si trajera antes, el pull pisaría con la versión del
    // servidor una cita que todavía no subió.
    await empujarPendientes()
    await traerCambios()
    estadoActual = 'inactivo'
    await escribirMeta('ultimo_sync_ok', new Date().toISOString())
  } catch (e) {
    estadoActual = navigator.onLine ? 'error' : 'sin_conexion'
    ultimoError = e instanceof Error ? e.message : String(e)
  } finally {
    corriendo = false
    void notificar()
    if (pendienteOtraVuelta) {
      pendienteOtraVuelta = false
      void sincronizar()
    }
  }
}

let intervalo: ReturnType<typeof setInterval> | null = null

export function arrancarSync(cadaMs = 30_000): () => void {
  void sincronizar()
  intervalo = setInterval(() => void sincronizar(), cadaMs)

  // Al volver la red se sincroniza en el acto: esperar al próximo tick haría
  // que la recepcionista vea "sin conexión" medio minuto después de que volvió.
  const alVolver = () => void sincronizar()
  window.addEventListener('online', alVolver)
  window.addEventListener('offline', alVolver)

  return () => {
    if (intervalo) clearInterval(intervalo)
    window.removeEventListener('online', alVolver)
    window.removeEventListener('offline', alVolver)
  }
}
